import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SONGS as MOCK_SONGS } from '../mock/mock';
import { t as tr } from '../lib/i18n';
import api, { getToken, setToken } from '../lib/api';

const AppCtx = createContext(null);

const LS = {
  get(k, d) { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch { return d; } },
  set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} }
};

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => LS.get('lang', 'fr'));
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [library, setLibrary] = useState([]);   // server-side library (when logged in)
  const [feed, setFeed] = useState([]);         // public feed from backend
  const [liked, setLiked] = useState(() => LS.get('liked', []));
  const [queue, setQueue] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const audioRef = useRef(null);

  useEffect(() => LS.set('lang', lang), [lang]);
  useEffect(() => LS.set('liked', liked), [liked]);

  // Audio element
  useEffect(() => {
    const a = new Audio();
    a.preload = 'metadata';
    // NOTE: do NOT set crossOrigin='anonymous' — some audio CDNs (e.g. soundhelix.com)
    // don't return CORS headers and would block playback entirely.
    // The service worker still caches audio via no-cors fetch (opaque responses) for offline use.
    a.volume = volume;
    audioRef.current = a;
    const onTime = () => setProgress(a.currentTime);
    const onMeta = () => setDuration(a.duration || 0);
    const onEnd = () => playNext();
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('ended', onEnd);
    return () => { a.pause(); a.src = ''; };
    // eslint-disable-next-line
  }, []);
  useEffect(() => { if (audioRef.current) audioRef.current.volume = volume; }, [volume]);

  // Bootstrap: try /auth/me, load feed
  useEffect(() => {
    (async () => {
      try {
        const t = getToken();
        if (t) {
          const me = await api.me();
          setUser(me.user);
          try { const mine = await api.mine(); setLibrary(mine.songs || []); } catch {}
        }
      } catch { setToken(null); }
      try { const f = await api.feed(); setFeed(f.songs || []); } catch {}
      setAuthReady(true);
    })();
  }, []);

  const refreshUser = useCallback(async () => {
    try { const me = await api.me(); setUser(me.user); } catch {}
  }, []);

  const refreshLibrary = useCallback(async () => {
    if (!user) return;
    try { const mine = await api.mine(); setLibrary(mine.songs || []); } catch {}
  }, [user]);

  // Poll any pending songs (Suno still generating) and update them in place.
  useEffect(() => {
    const pending = library.filter(s => s.sunoStatus === 'pending');
    if (pending.length === 0) return;
    const tick = async () => {
      const updates = await Promise.all(pending.map(async (s) => {
        try { const r = await api.refreshSong(s.id); return r.song; }
        catch { return null; }
      }));
      const updated = updates.filter(Boolean);
      if (!updated.length) return;
      setLibrary(prev => prev.map(s => {
        const u = updated.find(x => x.id === s.id);
        return u ? u : s;
      }));
      // If currently playing one of these and it became ready, swap the audio source live
      const a = audioRef.current;
      if (a && current) {
        const swapped = updated.find(x => x.id === current.id && x.sunoStatus === 'ready' && x.audio && a.src !== x.audio);
        if (swapped) {
          const wasPlaying = !a.paused;
          const t = a.currentTime || 0;
          a.src = swapped.audio;
          a.currentTime = Math.min(t, 1);
          if (wasPlaying) a.play().catch(()=>{});
          // also swap in queue
          setQueue(prev => prev.map(q => q.id === swapped.id ? swapped : q));
        }
      }
    };
    const handle = setInterval(tick, 8000);
    // run one immediate poll too
    tick();
    return () => clearInterval(handle);
    // eslint-disable-next-line
  }, [library.map(s => s.id + ':' + s.sunoStatus).join('|')]);

  const refreshFeed = useCallback(async () => {
    try { const f = await api.feed(); setFeed(f.songs || []); } catch {}
  }, []);

  const current = currentIdx >= 0 ? queue[currentIdx] : null;

  const playSong = (song, list) => {
    const newQueue = list && list.length ? list : [song];
    const idx = newQueue.findIndex(s => s.id === song.id);
    setQueue(newQueue);
    setCurrentIdx(idx >= 0 ? idx : 0);
    setTimeout(() => {
      const a = audioRef.current; if (!a) return;
      a.src = song.audio;
      a.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    }, 0);
  };

  const togglePlay = () => {
    const a = audioRef.current; if (!a || !current) return;
    if (a.paused) { a.play().then(()=>setIsPlaying(true)).catch(()=>setIsPlaying(false)); }
    else { a.pause(); setIsPlaying(false); }
  };

  const seek = (sec) => { const a = audioRef.current; if (!a) return; a.currentTime = sec; setProgress(sec); };
  const playNext = () => {
    if (!queue.length) return;
    const next = (currentIdx + 1) % queue.length;
    setCurrentIdx(next);
    const a = audioRef.current; if (!a) return;
    a.src = queue[next].audio;
    a.play().then(()=>setIsPlaying(true)).catch(()=>setIsPlaying(false));
  };
  const playPrev = () => {
    if (!queue.length) return;
    const prev = (currentIdx - 1 + queue.length) % queue.length;
    setCurrentIdx(prev);
    const a = audioRef.current; if (!a) return;
    a.src = queue[prev].audio;
    a.play().then(()=>setIsPlaying(true)).catch(()=>setIsPlaying(false));
  };

  const toggleLike = async (id) => {
    // optimistic local toggle
    setLiked(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
    if (user) {
      try { await api.likeSong(id); } catch {}
    }
  };

  const signIn = async (email, password) => {
    const data = await api.login(email, password);
    setToken(data.token); setUser(data.user);
    try { const mine = await api.mine(); setLibrary(mine.songs || []); } catch {}
  };
  const signUp = async (name, email, password) => {
    const data = await api.signup(name, email, password);
    setToken(data.token); setUser(data.user);
    setLibrary([]);
  };
  const signOut = () => { setToken(null); setUser(null); setLibrary([]); };

  const t = (path) => tr(lang, path);

  // public-facing combined feed (server + mock fallback)
  const combinedFeed = useMemo(() => {
    if (feed.length) return feed;
    return MOCK_SONGS;
  }, [feed]);

  const value = {
    lang, setLang, t,
    user, authReady, signIn, signUp, signOut,
    library, refreshLibrary, refreshFeed, refreshUser, feed: combinedFeed,
    liked, toggleLike,
    queue, currentIdx, current, isPlaying,
    progress, duration, volume, setVolume,
    playSong, togglePlay, playNext, playPrev, seek,
    api,
  };

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export const useApp = () => {
  const v = useContext(AppCtx);
  if (!v) throw new Error('useApp must be used inside AppProvider');
  return v;
};
